import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { LOCALES } from "./LocaleLink";

// Subtle first-visit nudge: if the visitor's browser is set to a language we
// support and they landed on a different locale, offer to switch (once per
// browser). Non-intrusive Sonner toast, dismissible.
const NUDGE_KEY = "vl_locale_nudge_v1";

const COPY = {
  tr: { message: "Bu siteyi Türkçe görüntüle", action: "Değiştir" },
  fr: { message: "Voir ce site en français", action: "Changer" },
  en: { message: "View this site in English", action: "Switch" },
};

export const LocaleNudge = () => {
  const { lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;
    if (typeof window === "undefined") return;
    // Don't nudge on the admin surface — the admin UI is English-only by design.
    if (location.pathname.startsWith("/admin")) return;
    // Skip if we've already nudged this browser (acted, dismissed, or auto-closed).
    if (localStorage.getItem(NUDGE_KEY)) return;

    const raw = (navigator.language || navigator.userLanguage || "").toLowerCase();
    const browserLang = raw.slice(0, 2);
    if (!LOCALES.includes(browserLang)) return;
    if (browserLang === lang) return;

    const copy = COPY[browserLang] || COPY.en;
    shown.current = true;

    // Small delay so it doesn't fight for attention with the hero on landing.
    // NOTE: intentionally no cleanup — we want the toast to fire even if this
    // effect re-runs due to router identity churn. `shown.current` already
    // prevents duplicate scheduling.
    setTimeout(() => {
      toast(copy.message, {
        duration: 8000,
        action: {
          label: copy.action,
          onClick: () => {
            localStorage.setItem(NUDGE_KEY, "acted");
            const parts = location.pathname.split("/");
            if (LOCALES.includes(parts[1])) parts[1] = browserLang;
            else parts.splice(1, 0, browserLang);
            navigate((parts.join("/") || "/") + location.search + location.hash);
            setLang(browserLang);
          },
        },
      });
      // Persist "seen" so we don't re-nudge on subsequent visits even if user ignores it.
      localStorage.setItem(NUDGE_KEY, "seen");
    }, 1400);
  }, [lang, location, navigate, setLang]);

  return null;
};

export default LocaleNudge;
